import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LayoutPanelLeft } from 'lucide-react';
import Layout from '../components/Layout';
import ClientCard from '../components/ClientCard';
import { useClient } from '../ClientContext';

const styles = {
  container: { padding: '48px' },
  header: { display: 'flex', alignItems: 'center', gap: '8px', mb: '32px' },
  welcomeSection: { mb: '32px' },
  welcomeText: { display: 'flex', gap: '8px', alignItems: 'baseline', mb: '8px' },
  welcomeTitle: { fontSize: '32px', fontWeight: 700 },
  dashboardContainer: { backgroundColor: '#FFFFFF', p: '32px', borderRadius: '8px' },
  dashboardTitle: { fontSize: '20px', fontWeight: 700, mb: '24px' },
  adminPanelTitle: { fontSize: '24px', fontWeight: 700 },
};

const AdminPage = ({ user, accessToken }) => {
  const { selectedClient, setSelectedClient, config } = useClient();
  const [error, setError] = useState(null);
  const [sxUsers, setSxUsers] = useState([]);
  const [tidalUsers, setTidalUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [iheartUsers, setIheartUsers] = useState([]);
  const [beatportUsers, setBeatportUsers] = useState([]);
  const [audiomackUsers, setAudiomackUsers] = useState([]);
  const [mlcUsers, setMlcUsers] = useState([]);
  const [mlcClaimsUsers, setMlcClaimsUsers] = useState([]);
  const [rocnationUsers, setRocnationUsers] = useState([]);
  const [sonyUsers, setSonyUsers] = useState([]);
  const [maintenanceStatuses, setMaintenanceStatuses] = useState({});

  const sxUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-sx`;
  const tidalUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-tidal`;
  const adminUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-admin`;
  const iheartUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-iheart`;
  const beatportUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-beatport`;
  const audiomackUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-audiomack`;
  const mlcUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-mlc`;
  const mlcClaimsUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-mlc-claims`;
  const rocnationUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-rocnation`;
  const sonyUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users?client=Sony`;

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    const fetchData = async (endpoint, setData) => {
      try {
        const response = await fetch(endpoint, {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch data');

        const data = await response.json();
        setData(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', err);
        setError('Error fetching data.');
      }
    };

    fetchData(sxUserEndpoint, (data) => setSxUsers(data.sxUsers || []));
    fetchData(tidalUserEndpoint, (data) => setTidalUsers(data.tidalUsers || []));
    fetchData(iheartUserEndpoint, (data) => setIheartUsers(data.iheartUsers || []));
    fetchData(adminUserEndpoint, (data) => setAdminUsers(data.adminUsers || []));
    fetchData(beatportUserEndpoint, (data) => setBeatportUsers(data.beatportUsers || []));
    fetchData(audiomackUserEndpoint, (data) => setAudiomackUsers(data.audiomackUsers || []));
    fetchData(mlcUserEndpoint, (data) => setMlcUsers(data.mlcUsers || []));
    fetchData(mlcClaimsUserEndpoint, (data) => setMlcClaimsUsers(data.users || []));
    fetchData(rocnationUserEndpoint, (data) => setRocnationUsers(data.rocnationUsers || []));
    fetchData(sonyUserEndpoint, (data) => setSonyUsers(data.users || []));
  }, [
    sxUserEndpoint,
    tidalUserEndpoint,
    adminUserEndpoint,
    iheartUserEndpoint,
    beatportUserEndpoint,
    audiomackUserEndpoint,
    mlcUserEndpoint,
    mlcClaimsUserEndpoint,
    rocnationUserEndpoint,
    sonyUserEndpoint,
    accessToken,
  ]);

  useEffect(() => {
    const clientsToFetch = ['Tidal', 'iHeart', 'Beatport', 'Audiomack'];
    const fetchMaintenanceStatuses = async () => {
      if (!accessToken) return;
      const statuses = {};
      try {
        for (const client of clientsToFetch) {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/admin/maintenance-mode?client=${encodeURIComponent(client)}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            },
          );
          if (response.ok) {
            const data = await response.json();
            statuses[client] = data.data.isEnabled;
          } else {
            statuses[client] = null;
          }
        }
        setMaintenanceStatuses(statuses);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching maintenance statuses:', err);
      }
    };
    fetchMaintenanceStatuses();
  }, [accessToken]);

  const countUsers = (
    sxUsers,
    tidalUsers,
    admins,
    iheartUsers,
    beatportUsers = [],
    audiomackUsers = [],
    mlcUsers = [],
    mlcClaimsUsers = [],
    rocnationUsers = [],
    sonyUsers = [],
  ) => {
    let tidalOnlyUsers = 0;
    let sxOnlyUsers = 0;
    let iheartOnlyUsers = 0;
    let beatportOnlyUsers = 0;
    let audiomackOnlyUsers = 0;
    let mlcOnlyUsers = 0;
    let mlcClaimsOnlyUsers = 0;
    let rocnationOnlyUsers = 0;
    let sonyOnlyUsers = 0;
    let beatdappUsersTidal = 0;
    let beatdappUsersIheart = 0;
    let beatdappUsersSx = 0;
    let beatdappUsersBeatport = 0;
    let beatdappUsersAudiomack = 0;
    let beatdappUsersMlc = 0;
    let beatdappUsersMlcClaims = 0;
    let beatdappUsersRocnation = 0;
    let beatdappUsersSony = 0;
    const totalAdmins = admins.length;

    sxUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersSx++;
      } else {
        sxOnlyUsers++;
      }
    });

    tidalUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersTidal++;
      } else {
        tidalOnlyUsers++;
      }
    });

    iheartUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersIheart++;
      } else {
        iheartOnlyUsers++;
      }
    });

    beatportUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersBeatport++;
      } else {
        beatportOnlyUsers++;
      }
    });

    audiomackUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersAudiomack++;
      } else {
        audiomackOnlyUsers++;
      }
    });

    mlcUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersMlc++;
      } else {
        mlcOnlyUsers++;
      }
    });

    mlcClaimsUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersMlcClaims++;
      } else {
        mlcClaimsOnlyUsers++;
      }
    });

    rocnationUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersRocnation++;
      } else {
        rocnationOnlyUsers++;
      }
    });

    sonyUsers.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsersSony++;
      } else {
        sonyOnlyUsers++;
      }
    });

    const totalUsersTidal = tidalOnlyUsers + beatdappUsersTidal + totalAdmins;
    const totalUsersSx = sxOnlyUsers + beatdappUsersSx + totalAdmins;
    const totalUsersIheart = iheartOnlyUsers + beatdappUsersIheart + totalAdmins;
    const totalUsersBeatport = beatportOnlyUsers + beatdappUsersBeatport + totalAdmins;
    const totalUsersAudiomack = audiomackOnlyUsers + beatdappUsersAudiomack + totalAdmins;
    const totalUsersMlc = mlcOnlyUsers + beatdappUsersMlc + totalAdmins;
    const totalUsersMlcClaims = mlcClaimsOnlyUsers + beatdappUsersMlcClaims + totalAdmins;
    const totalUsersRocnation = rocnationOnlyUsers + beatdappUsersRocnation + totalAdmins;
    const totalUsersSony = sonyOnlyUsers + beatdappUsersSony + totalAdmins;

    return {
      tidalStats: {
        totalUsers: totalUsersTidal,
        tidalOnlyUsers,
        beatdappUsers: beatdappUsersTidal,
        totalAdmins,
      },
      sxStats: {
        totalUsers: totalUsersSx,
        sxOnlyUsers,
        beatdappUsers: beatdappUsersSx,
        totalAdmins,
      },
      iheartStats: {
        totalUsers: totalUsersIheart,
        iheartOnlyUsers,
        beatdappUsers: beatdappUsersIheart,
        totalAdmins,
      },
      beatportStats: {
        totalUsers: totalUsersBeatport,
        beatportOnlyUsers,
        beatdappUsers: beatdappUsersBeatport,
        totalAdmins,
      },
      audiomackStats: {
        totalUsers: totalUsersAudiomack,
        audiomackOnlyUsers,
        beatdappUsers: beatdappUsersAudiomack,
        totalAdmins,
      },
      mlcStats: {
        totalUsers: totalUsersMlc,
        mlcOnlyUsers,
        beatdappUsers: beatdappUsersMlc,
        totalAdmins,
      },
      mlcClaimsStats: {
        totalUsers: totalUsersMlcClaims,
        mlcClaimsOnlyUsers,
        beatdappUsers: beatdappUsersMlcClaims,
        totalAdmins,
      },
      rocnationStats: {
        totalUsers: totalUsersRocnation,
        rocnationOnlyUsers,
        beatdappUsers: beatdappUsersRocnation,
        totalAdmins,
      },
      sonyStats: {
        totalUsers: totalUsersSony,
        sonyOnlyUsers,
        beatdappUsers: beatdappUsersSony,
        totalAdmins,
      },
    };
  };

  const {
    tidalStats,
    sxStats,
    iheartStats,
    beatportStats,
    audiomackStats,
    mlcStats,
    // mlcClaimsStats, TODO: Uncomment this
    rocnationStats,
    sonyStats,
  } = countUsers(
    sxUsers,
    tidalUsers,
    adminUsers,
    iheartUsers,
    beatportUsers,
    audiomackUsers,
    mlcUsers,
    mlcClaimsUsers,
    rocnationUsers,
    sonyUsers,
  );

  const formatStats = (stats, type) => [
    { label: 'Total Users', value: stats.totalUsers ?? 0 },
    {
      label: (() => {
        switch (type) {
          case 'TIDAL':
            return 'Tidal Only Users';
          case 'SX':
            return 'SX Only Users';
          case 'MLC':
            return 'MLC Only Users';
          case 'MLC_CLAIMS':
            return 'MLC Claims Only Users';
          case 'ROCNATION':
            return 'RN Only Users';
          case 'IHEART':
            return 'iHeart Only Users';
          case 'BEATPORT':
            return 'Beatport Only Users';
          case 'AUDIOMACK':
            return 'AM Only Users';
          case 'SONY':
            return 'Sony Only Users';
          case 'AIMD':
            return 'AIMD Users';
          default:
            return 'Dashboard Users';
        }
      })(),
      value: (() => {
        switch (type) {
          case 'TIDAL':
            return stats.tidalOnlyUsers ?? 0;
          case 'SX':
            return stats.sxOnlyUsers ?? 0;
          case 'MLC':
            return stats.mlcOnlyUsers ?? 0;
          case 'MLC_CLAIMS':
            return stats.mlcClaimsOnlyUsers ?? 0;
          case 'ROCNATION':
            return stats.rocnationOnlyUsers ?? 0;
          case 'IHEART':
            return stats.iheartOnlyUsers ?? 0;
          case 'BEATPORT':
            return stats.beatportOnlyUsers ?? 0;
          case 'AUDIOMACK':
            return stats.audiomackOnlyUsers ?? 0;
          case 'SONY':
            return stats.sonyOnlyUsers ?? 0;
          case 'AIMD':
            return stats.aimdUsers ?? 0;
          default:
            return 0;
        }
      })(),
    },
    { label: 'Beatdapp Users', value: stats.beatdappUsers ?? 0 },
    { label: 'Total Admins', value: stats.totalAdmins ?? 0 },
  ];

  const DASHBOARDS = {
    TIDAL: {
      logo: config?.clients?.Tidal?.logo || '/assets/tidal-logo.svg',
      name: 'TIDAL',
      description: 'Tidal Dashboard',
      envKey: 'REACT_APP_DSP_DASH_URL',
      stagingUrl: config?.clients?.Tidal?.stagingUrl || process.env.REACT_APP_DSP_DASH_URL_STG,
      adminUrl: config?.clients?.Tidal?.adminUrl || process.env.REACT_APP_DSP_DASH_ADMIN_URL,
      prodUrl: config?.clients?.Tidal?.prodUrl || process.env.REACT_APP_DSP_DASH_URL,
      stats: formatStats(tidalStats, 'TIDAL'),
      maintenanceStatus: maintenanceStatuses['Tidal'],
    },
    SOUND_EXCHANGE: {
      logo: config?.clients?.SoundExchange?.logo || '/assets/sound-exchange-logo.svg',
      name: 'SoundExchange',
      description: 'SoundExchange Dashboard',
      envKey: 'REACT_APP_SX_DASH_URL',
      stagingUrl: config?.clients?.SoundExchange?.stagingUrl || process.env.REACT_APP_SX_DASH_URL_STG,
      adminUrl: config?.clients?.SoundExchange?.adminUrl || process.env.REACT_APP_SX_DASH_ADMIN_URL,
      prodUrl: config?.clients?.SoundExchange?.prodUrl || process.env.REACT_APP_SX_DASH_URL,
      stats: formatStats(sxStats, 'SX'),
    },
    MLC: {
      logo: config?.clients?.MLC?.logo || '/assets/mlc-logo.png',
      name: 'MLC',
      description: 'MLC Dashboard',
      envKey: 'REACT_APP_MLC_DASH_URL',
      stagingUrl: config?.clients?.MLC?.stagingUrl || process.env.REACT_APP_MLC_DASH_URL_STG,
      adminUrl: config?.clients?.MLC?.adminUrl || process.env.REACT_APP_MLC_DASH_ADMIN_URL,
      prodUrl: config?.clients?.MLC?.prodUrl || process.env.REACT_APP_MLC_DASH_URL,
      stats: formatStats(mlcStats, 'MLC'),
    },
    // MLC_CLAIMS: {  TODO: Uncomment this
    //   logo: config?.clients?.['MLC Claims']?.logo || '/assets/mlc-logo.png',
    //   name: 'MLC Claims',
    //   description: 'MLC Claims Dashboard',
    //   envKey: 'REACT_APP_CLAIMS_DASH_URL',
    //   stagingUrl: config?.clients?.['MLC Claims']?.stagingUrl || process.env.REACT_APP_CLAIMS_DASH_URL_STG,
    //   adminUrl: config?.clients?.['MLC Claims']?.adminUrl || process.env.REACT_APP_CLAIMS_DASH_ADMIN_URL,
    //   prodUrl: config?.clients?.['MLC Claims']?.prodUrl || process.env.REACT_APP_CLAIMS_DASH_URL,
    //   stats: formatStats(mlcClaimsStats, 'MLC_CLAIMS'),
    // },
    ROCNATION: {
      logo: config?.clients?.RocNation?.logo || '/assets/roc-nation-logo.png',
      name: 'RocNation',
      description: 'RocNation Dashboard',
      envKey: 'REACT_APP_ROCNATION_DASH_URL',
      stagingUrl: config?.clients?.RocNation?.stagingUrl || process.env.REACT_APP_ROCNATION_DASH_URL_STG,
      adminUrl: config?.clients?.RocNation?.adminUrl || process.env.REACT_APP_ROCNATION_DASH_ADMIN_URL,
      prodUrl: config?.clients?.RocNation?.prodUrl || process.env.REACT_APP_ROCNATION_DASH_URL,
      stats: formatStats(rocnationStats, 'ROCNATION'),
    },
    IHEART: {
      logo: config?.clients?.iHeart?.logo || '/assets/iHeart-logo.svg',
      name: 'iHeart',
      description: 'iHeart Dashboard',
      envKey: 'REACT_APP_IHEART_DASH_URL',
      stagingUrl: config?.clients?.iHeart?.stagingUrl || process.env.REACT_APP_IHEART_DASH_URL_STG,
      adminUrl: config?.clients?.iHeart?.adminUrl || process.env.REACT_APP_IHEART_DASH_ADMIN_URL,
      prodUrl: config?.clients?.iHeart?.prodUrl || process.env.REACT_APP_IHEART_DASH_URL,
      stats: formatStats(iheartStats, 'IHEART'),
      maintenanceStatus: maintenanceStatuses['iHeart'],
    },
    BEATPORT: {
      logo: config?.clients?.Beatport?.logo || '/assets/beatport-logo.png',
      name: 'Beatport',
      description: 'Beatport Dashboard',
      envKey: 'REACT_APP_BEATPORT_DASH_URL',
      stagingUrl: config?.clients?.Beatport?.stagingUrl || process.env.REACT_APP_BEATPORT_DASH_URL_STG,
      adminUrl: config?.clients?.Beatport?.adminUrl || process.env.REACT_APP_BEATPORT_DASH_ADMIN_URL,
      prodUrl: config?.clients?.Beatport?.prodUrl || process.env.REACT_APP_BEATPORT_DASH_URL,
      stats: formatStats(beatportStats, 'BEATPORT'),
      maintenanceStatus: maintenanceStatuses['Beatport'],
    },
    AUDIOMACK: {
      logo: config?.clients?.Audiomack?.logo || '/assets/audiomack-logo.png',
      name: 'Audiomack',
      description: 'Audiomack Dashboard',
      envKey: 'REACT_APP_AUDIOMACK_DASH_URL',
      stagingUrl: config?.clients?.Audiomack?.stagingUrl || process.env.REACT_APP_AUDIOMACK_DASH_URL_STG,
      adminUrl: config?.clients?.Audiomack?.adminUrl || process.env.REACT_APP_AUDIOMACK_DASH_ADMIN_URL,
      prodUrl: config?.clients?.Audiomack?.prodUrl || process.env.REACT_APP_AUDIOMACK_DASH_URL,
      stats: formatStats(audiomackStats, 'AUDIOMACK'),
      maintenanceStatus: maintenanceStatuses['Audiomack'],
    },
    SONY: {
      logo: config?.clients?.Sony?.logo || '/assets/sony-logo.png',
      name: 'Sony',
      description: 'Sony Dashboard',
      envKey: 'REACT_APP_SONY_DASH_URL',
      stagingUrl: config?.clients?.Sony?.stagingUrl || process.env.REACT_APP_SONY_DASH_URL_STG,
      adminUrl: config?.clients?.Sony?.adminUrl || process.env.REACT_APP_SONY_DASH_ADMIN_URL,
      prodUrl: config?.clients?.Sony?.prodUrl || process.env.REACT_APP_SONY_DASH_URL,
      stats: formatStats(sonyStats, 'SONY'),
    },
    AIMD: {
      logo: config?.clients?.AIMD?.logo || '/assets/beatdapp-logo-dark.svg',
      name: 'AIMD',
      description: 'AI Music Detection Dashboard',
      stagingUrl: config?.clients?.AIMD?.stagingUrl,
      adminUrl: config?.clients?.AIMD?.adminUrl,
      prodUrl: config?.clients?.AIMD?.prodUrl,
      stats: formatStats(
        {
          totalUsers: 0,
          aimdUsers: 0,
          beatdappUsers: 0,
          totalAdmins: 0,
        },
        'AIMD',
      ),
    },
  };

  // Helper function to map dashboard keys to client names using config
  const getClientNameFromDashboardKey = (dashboardKey) => {
    return config?.dashboardKeyToClientName?.[dashboardKey] || null;
  };

  const DashboardSection = () => {
    let dashboardsToShow;

    switch (selectedClient) {
      case 'All Dashboards':
        dashboardsToShow = DASHBOARDS;
        break;
      case 'Tidal':
        dashboardsToShow = { TIDAL: DASHBOARDS.TIDAL };
        break;
      case 'iHeart':
        dashboardsToShow = { IHEART: DASHBOARDS.IHEART };
        break;
      case 'SoundExchange':
        dashboardsToShow = { SOUND_EXCHANGE: DASHBOARDS.SOUND_EXCHANGE };
        break;
      case 'MLC':
        dashboardsToShow = { MLC: DASHBOARDS.MLC };
        break;
      // case 'MLC Claims':  TODO: Uncomment this
      //   dashboardsToShow = { MLC_CLAIMS: DASHBOARDS.MLC_CLAIMS };
      //   break;
      case 'RocNation':
        dashboardsToShow = { ROCNATION: DASHBOARDS.ROCNATION };
        break;
      case 'Beatport':
        dashboardsToShow = { BEATPORT: DASHBOARDS.BEATPORT };
        break;
      case 'Audiomack':
        dashboardsToShow = { AUDIOMACK: DASHBOARDS.AUDIOMACK };
        break;
      case 'Sony':
        dashboardsToShow = { SONY: DASHBOARDS.SONY };
        break;
      case 'AIMD':
        dashboardsToShow = { AIMD: DASHBOARDS.AIMD };
        break;
      default:
        dashboardsToShow = {};
    }

    return (
      <>
        {Object.entries(dashboardsToShow).map(([key, dashboard]) => (
          <ClientCard
            key={key}
            logo={dashboard.logo}
            name={dashboard.name}
            description={dashboard.description}
            stats={dashboard.stats}
            buttonLabel="Go to Dashboard"
            onButtonClick={
              dashboard.prodUrl
                ? () => {
                    const clientName = getClientNameFromDashboardKey(key);
                    if (clientName) setSelectedClient(clientName);
                    window.location.href = dashboard.prodUrl;
                  }
                : undefined
            }
            onGoToStaging={
              dashboard.stagingUrl
                ? () => {
                    const clientName = getClientNameFromDashboardKey(key);
                    if (clientName) setSelectedClient(clientName);
                    window.location.href = dashboard.stagingUrl;
                  }
                : undefined
            }
            onOpenAdmin={
              dashboard.adminUrl
                ? () => {
                    const clientName = getClientNameFromDashboardKey(key);
                    if (clientName) setSelectedClient(clientName);
                    window.location.href = dashboard.adminUrl;
                  }
                : undefined
            }
            maintenanceStatus={dashboard.maintenanceStatus}
          />
        ))}
      </>
    );
  };

  if (error) return <div>{error}</div>;

  const formatName = (nickname) => {
    if (!nickname) return '';

    if (!nickname.includes('.')) {
      return nickname;
    }

    const [first, last] = nickname.split('.');
    return `${first.charAt(0).toUpperCase() + first.slice(1)} ${last.charAt(0).toUpperCase() + last.slice(1)}`;
  };

  return (
    <Layout profilePic={user.picture}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <LayoutPanelLeft size={24} />
          <Typography sx={styles.adminPanelTitle}>Admin Panel</Typography>
        </Box>
        <Box sx={styles.welcomeSection}>
          <Box sx={styles.welcomeText}>
            <Typography sx={styles.welcomeTitle}>Welcome {formatName(user?.nickname)}</Typography>
            <Typography variant="body2" gutterBottom>
              ({user.email})
            </Typography>
          </Box>
          <Typography variant="body2" gutterBottom>
            An overview of all dashboards that you can access
          </Typography>
        </Box>
        <Box sx={styles.dashboardContainer}>
          <Typography sx={styles.dashboardTitle}>Dashboards</Typography>
          <DashboardSection />
        </Box>
      </Box>
    </Layout>
  );
};

export default AdminPage;
